import React, { useContext, useEffect } from 'react';
import { t } from 'i18next';
import { ConnectionStatus, Network } from '@capacitor/network';
import { useHistory, useLocation } from 'react-router';
import axios from 'axios';
import { UserDataProp } from '../../models/userDataProp';
import { UserContext } from '../../providers/UserProvider';
import Users from '../../schemas/public/Users';
import { setAccessToken, setRefreshToken, clearTokens } from '../../utilities/storage/tokenStorage';
import { AuthApiErrorCode } from '../../models/auth';
import type { AuthTokens } from '../../models/auth';
import './login.css';
import { LoginForm } from './LoginForm';
import { SingUpForm } from './SingUpForm';
import { ForgotPasswordForm } from './ForgotPasswordForm';
import { InfoModal } from './InfoModal';
import { validateEmail } from '../../utilities/utils';
import {
    formatRateLimitedMessage,
    getRateLimitRetryAfter,
    isRateLimitedError
} from '../../utilities/rateLimitError';
import { API_URL } from '../../config';
import { XimiIdentityErrorCode } from '../../stores/ximi/ximiStimulinIdentity';
import { XimiSessionLostCode } from '../../stores/ximi/ximiStimulinSession';

/** Message (clé i18n) du bandeau réseau — sert aussi de sentinelle pour ne nettoyer que lui. */
const NETWORK_OFFLINE_MESSAGE =
    'Aucun accès à internet détecté. Veuillez vérifier votre connexion.';

/**
 * Message affiché selon la cause du refus Ximi. Dire « votre agence n'a pas activé le service »
 * plutôt que « réessayez » évite à l'utilisateur de recommencer une action vouée à échouer.
 */
const XIMI_ERROR_MESSAGES: Record<string, string> = {
    [XimiIdentityErrorCode.EMAIL_MISSING]:
        "Votre compte Ximi n'a pas d'adresse e-mail. Contactez votre agence.",
    [XimiIdentityErrorCode.SUBSCRIPTION_INACTIVE]:
        "Le service Stimul'In n'est pas activé pour votre structure.",
    [XimiSessionLostCode.DEVICE_EVICTED]:
        'Votre compte a été connecté sur un autre appareil. Reconnectez-vous pour reprendre la main.',
    [AuthApiErrorCode.SESSION_PROOF_INVALID]: 'Votre session a expiré. Veuillez vous reconnecter.',
    [XimiSessionLostCode.ACCESS_REFUSED]:
        "Votre accès à Stimul'In n'est pas activé. Contactez votre agence."
};

const XIMI_ERROR_FALLBACK = 'La connexion Ximi a échoué. Veuillez réessayer.';

export function Login(): JSX.Element {
    const history = useHistory();
    const location = useLocation();
    const userDataProp: UserDataProp | null = useContext(UserContext);
    const [displayPage, setDisplayPage] = React.useState<'login' | 'singup' | 'forgot'>('login');
    const [displayInfoModal, setDisplayInfoModal] = React.useState(false);
    const [msgError, setMsgError] = React.useState<string | null>();
    const [msgForgotPassword, setMsgForgotPassword] = React.useState<string | null>();
    const [isLoggingIn, setIsLoggingIn] = React.useState(false);

    useEffect(() => {
        if (msgForgotPassword !== '' && msgForgotPassword !== null) {
            setTimeout(() => {
                setMsgForgotPassword('');
            }, 8000);
        }
    }, [msgForgotPassword]);

    useEffect(() => {
        const ximiError = new URLSearchParams(location.search).get('ximiError');
        if (!ximiError) return;
        // Un code inconnu (ou le générique « 1 ») retombe sur le message par défaut.
        setMsgError(t(XIMI_ERROR_MESSAGES[ximiError] ?? XIMI_ERROR_FALLBACK));
    }, []);

    const showRateLimitedError = (err: unknown): void => {
        setMsgError(formatRateLimitedMessage(getRateLimitRetryAfter(err)));
    };

    const networkCheck = (status: ConnectionStatus) => {
        if (!status.connected) {
            setMsgError(t(NETWORK_OFFLINE_MESSAGE));
            return false;
        }
        // Ne nettoie QUE le message réseau — pas les erreurs posées par d'autres flows (ximiError…)
        setMsgError((prev) => (prev === t(NETWORK_OFFLINE_MESSAGE) ? '' : prev));
        return true;
    };

    useEffect(() => {
        Network.getStatus().then((status) => {
            networkCheck(status);
        });
        Network.addListener('networkStatusChange', (status) => {
            networkCheck(status);
        });
    }, []);

    const login = async (email: string, pass: string) => {
        setMsgError('');
        if (!email || !pass) {
            setMsgError(t('Veuillez renseigner tous les champs'));
            return;
        }
        setIsLoggingIn(true);
        try {
            const { data } = await axios.post<AuthTokens>(`${API_URL}/auth/login`, {
                email,
                password: pass
            });

            await setAccessToken(data.accessToken);
            await setRefreshToken(data.refreshToken);

            let loadedUser: Users | null = null;
            if (userDataProp?.refreshUser) {
                loadedUser = await userDataProp.refreshUser(data.accessToken);
            }

            if (loadedUser) {
                history.push('/home');
            } else {
                await clearTokens();
                setMsgError(t('Utilisateur non trouvé. Veuillez contacter le support.'));
            }
        } catch (err: any) {
            const code = err?.response?.data?.code;
            if (isRateLimitedError(err)) {
                showRateLimitedError(err);
            } else if (code === AuthApiErrorCode.USE_SSO) {
                setMsgError(
                    t(
                        'Vous tentez de vous connecter avec un compte Ximi. Utilisez le bouton « Connexion Ximi » ci-dessous.'
                    )
                );
            } else if (code === AuthApiErrorCode.PASSWORD_RESET_REQUIRED) {
                setDisplayPage('forgot');
                setMsgError(t('Vous devez réinitialiser votre mot de passe pour continuer.'));
            } else if (code === AuthApiErrorCode.ACCOUNT_LOCKED) {
                setMsgError(t('Compte temporairement verrouillé. Réessayez dans 15 minutes.'));
            } else if (code === AuthApiErrorCode.ACCOUNT_DISABLED) {
                setMsgError(t('Votre compte a été désactivé. Contactez le support.'));
            } else {
                setMsgError(err?.response?.data?.error || t('Email ou mot de passe incorrect'));
            }
        } finally {
            setIsLoggingIn(false);
        }
    };

    const register = async (email: string, pass: string, codeBe: string, cgu: boolean) => {
        setMsgError('');
        if (!email || !pass || !codeBe) {
            setMsgError(t('Veuillez renseigner tous les champs'));
            return;
        }
        if (!validateEmail(email)) {
            setMsgError(t('Veuillez renseigner un email valide'));
            return;
        }
        if (!cgu) {
            setMsgError(t('Veuillez accepter les CGU'));
            return;
        }
        if (pass.length < 6) {
            setMsgError(t('Le mot de passe doit contenir au moins 6 caractères'));
            return;
        }

        try {
            const { data } = await axios.post<AuthTokens>(`${API_URL}/auth/register`, {
                email,
                password: pass,
                codeBe
            });

            await setAccessToken(data.accessToken);
            await setRefreshToken(data.refreshToken);

            let loadedUser: Users | null = null;
            if (userDataProp?.refreshUser) {
                loadedUser = await userDataProp.refreshUser(data.accessToken);
            }
            if (loadedUser) {
                history.push('/home');
            }
        } catch (err: any) {
            const code = err?.response?.data?.code;
            if (isRateLimitedError(err)) {
                showRateLimitedError(err);
            } else if (code === AuthApiErrorCode.EMAIL_TAKEN) {
                setMsgError(t('Cet email est déjà utilisé'));
            } else if (code === AuthApiErrorCode.INVALID_CODE) {
                setMsgError(t('Le code Bonne Etoile est invalide'));
            } else {
                setMsgError(err?.response?.data?.error || t("Erreur lors de l'inscription"));
            }
        }
    };

    const forgotPassword = async (email: string) => {
        setMsgError('');
        if (!email) {
            setMsgError(t('Veuillez renseigner un email'));
            return;
        }
        if (!validateEmail(email)) {
            setMsgError(t('Veuillez renseigner un email valide'));
            return;
        }
        try {
            await axios.post(`${API_URL}/auth/request-password-reset`, { email });
        } catch (err) {
            // 429: otherwise the user would believe the code was sent when it was not
            if (isRateLimitedError(err)) {
                showRateLimitedError(err);
                return;
            }
            // Always show success for security
        }
    };

    const resetPassword = async (email: string, code: string, newPassword: string) => {
        setMsgError('');
        try {
            await axios.post(`${API_URL}/auth/reset-password`, {
                email,
                code,
                password: newPassword
            });
            setMsgForgotPassword(t('Mot de passe réinitialisé avec succès !'));
            setDisplayPage('login');
        } catch (err: any) {
            const errCode = err?.response?.data?.code;
            if (isRateLimitedError(err)) {
                showRateLimitedError(err);
                return;
            }
            if (errCode === AuthApiErrorCode.USE_SSO) {
                setMsgError(
                    t(
                        'Vous tentez de vous connecter avec un compte Ximi. Utilisez le bouton « Connexion Ximi ».'
                    )
                );
                setDisplayPage('login');
                return;
            }
            if (errCode === AuthApiErrorCode.CODE_ATTEMPTS_EXCEEDED) {
                setMsgError(
                    t(
                        "Trop d'essais avec ce code. Demandez un nouveau code avec « Mot de passe oublié »."
                    )
                );
                return;
            }
            if (errCode === AuthApiErrorCode.INVALID_CODE) {
                const remainingAttempts = err?.response?.data?.remainingAttempts;
                if (typeof remainingAttempts !== 'number') {
                    setMsgError(t('Code invalide ou expiré'));
                    return;
                }
                setMsgError(
                    remainingAttempts === 1
                        ? t('Code invalide. Il vous reste 1 essai.')
                        : t('Code invalide. Il vous reste {{count}} essais.', {
                              count: remainingAttempts
                          })
                );
                return;
            }
            setMsgError(err?.response?.data?.error || t('Code invalide ou expiré'));
        }
    };

    return (
        <>
            <div
                className={`login-container${
                    displayPage === 'forgot' ? ' login-container--top' : ''
                }`}>
                <div className={`login-box${displayPage === 'forgot' ? ' login-box--full' : ''}`}>
                    <div className="login-header">
                        <svg
                            height="5rem"
                            viewBox="0 0 20225 22971"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M16840.9 481.134C16929.8 290.466 17201 290.466 17289.9 481.134L18032.6 2072.44C18057.2 2125.18 18099.6 2167.58 18152.4 2192.19L19743.7 2934.86C19934.3 3023.84 19934.3 3294.96 19743.7 3383.95L18152.4 4126.61C18099.6 4151.23 18057.2 4193.62 18032.6 4246.36L17289.9 5837.67C17201 6028.34 16929.8 6028.34 16840.9 5837.67L16098.2 4246.36C16073.6 4193.62 16031.2 4151.23 15978.4 4126.61L14387.1 3383.95C14196.5 3294.96 14196.5 3023.84 14387.1 2934.86L15978.4 2192.19C16031.2 2167.58 16073.6 2125.18 16098.2 2072.44L16840.9 481.134Z"
                                fill="#E5007B"
                            />
                            <path
                                d="M14610 17197.2C14610 18824.2 13997 20139.2 12771.2 21142.2C11545.3 22122.9 9907.13 22613.2 7856.61 22613.2C6831.35 22613.2 5806.09 22490.7 4780.83 22245.5C3777.86 22000.3 2964.34 21677.1 2340.27 21275.9V17698.7C2630.02 17966.1 2975.48 18211.3 3376.67 18434.2C3800.15 18634.8 4279.35 18824.2 4814.27 19002.5C5260.03 19136.3 5716.94 19247.7 6184.99 19336.9C6653.05 19403.7 7132.25 19437.2 7622.59 19437.2C8670.14 19437.2 9483.66 19281.1 10063.2 18969.1C10642.6 18634.8 10932.4 18166.7 10932.4 17564.9C10932.4 17074.6 10720.7 16628.8 10297.2 16227.6C9873.7 15826.5 8948.74 15325 7522.29 14723.2C6452.45 14255.1 5661.22 13876.2 5148.59 13586.5C4658.25 13296.7 4234.77 12995.8 3878.16 12683.8C3298.66 12193.5 2864.04 11647.4 2574.29 11045.6C2306.84 10421.6 2173.11 9730.62 2173.11 8972.82C2173.11 7345.77 2763.75 6053.05 3945.02 5094.66C5148.59 4136.26 6786.78 3657.07 8859.59 3657.07C9840.27 3657.07 10787.5 3779.65 11701.3 4024.82C12615.2 4247.71 13361.8 4537.45 13941.3 4894.06V8237.3C13138.9 7791.54 12280.8 7446.07 11367 7200.9C10475.5 6955.73 9606.24 6833.14 8759.29 6833.14C7823.18 6833.14 7098.81 7000.31 6586.18 7334.63C6095.84 7668.95 5850.67 8125.86 5850.67 8705.36C5850.67 9195.7 6062.41 9641.47 6485.89 10042.7C6909.36 10421.6 7834.33 10911.9 9260.77 11513.7C10330.6 11959.4 11110.7 12327.2 11601 12616.9C12113.7 12906.7 12548.3 13207.6 12904.9 13519.6C13484.4 14010 13907.9 14556 14175.3 15157.8C14465.1 15759.6 14610 16439.4 14610 17197.2Z"
                                fill="#0F0046"
                            />
                        </svg>
                    </div>
                    {msgError && <div id="error-message">{msgError}</div>}
                    {msgForgotPassword && <div id="forgot-message">{msgForgotPassword}</div>}
                    {displayPage === 'login' && (
                        <LoginForm
                            login={login}
                            setDisplayPage={setDisplayPage}
                            isLoggingIn={isLoggingIn}
                        />
                    )}
                    {displayPage === 'singup' && (
                        <SingUpForm
                            register={register}
                            setDisplayPage={setDisplayPage}
                            setDisplayInfoModal={setDisplayInfoModal}
                        />
                    )}
                    {displayPage === 'forgot' && (
                        <ForgotPasswordForm
                            setDisplayPage={setDisplayPage}
                            forgotPassword={forgotPassword}
                            resetPassword={resetPassword}
                        />
                    )}
                </div>
            </div>
            {displayInfoModal && <InfoModal setDisplayInfoModal={setDisplayInfoModal} />}
        </>
    );
}
